/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { GroupsSortFields, SortOrder } from "~/generated/graphql";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination/Pagination";
import {
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput";
import { SidebarSelector } from "~/components/SidebarWithMultiSelect/SidebarSelector";
import { useSearchState } from "~/hooks/useSearchState";
import { PlanField } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { AddResourceSidebarState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/types";
import {
  formatSidebarOptions,
  getSelectedOptions,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/utils";
import { updateEntities } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/utils";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { useGetResourceGroups } from "~/views/resourceGroups/hooks/useGetResourceGroups/useGetResourceGroups";

type Props = {
  refetch: () => void;
  sidebarState: AddResourceSidebarState;
  updateSidebarState: (state: AddResourceSidebarState) => void;
};

export function AddResourceGroupSidebar({
  refetch,
  sidebarState,
  updateSidebarState,
}: Props) {
  const { setPlanData } = usePlanWizard();
  const { stepState, updateStepState } = useStepState();

  const { searchState, updateSearchState } = useSearchState({
    page: 1,
    limit: 20,
    sort: { field: GroupsSortFields.Name, order: SortOrder.Asc },
    filter: { phrase: "" },
  });

  const phrase = searchState.filter?.phrase ?? "";
  const trimmedPhrase = phrase.trim();
  const meetsMin = trimmedPhrase.length >= minSearchLength;
  const phraseToSend = meetsMin ? trimmedPhrase : undefined;
  const isSearchAllowed = trimmedPhrase.length === 0 || meetsMin;

  const { data, loading, count } = useGetResourceGroups({
    page: String(searchState.page),
    limit: String(searchState.limit),
    sort: {
      field:
        (searchState.sort?.field as GroupsSortFields) ?? GroupsSortFields.Name,
      order: searchState.sort?.order ?? SortOrder.Asc,
    },
    filter: { phrase: phraseToSend },
    basic: true,
  });

  const resourceGroupOptions = useMemo(
    () => formatSidebarOptions(data),
    [data]
  );

  const handleClose = () => {
    updateSidebarState({ activeSidebar: undefined });
  };

  const handleSubmit = () => {
    const updatedResourcesIds = {
      resourceGroupIds: sidebarState.selectedResourceGroupIds,
    };

    const updatedEntities = updateEntities(stepState, updatedResourcesIds);

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        ...updatedResourcesIds,
      },
    });

    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        ...prevPlanData[PlanField.PLAN_SETTINGS],
        planEntities: updatedEntities,
      },
    }));

    handleClose();
    updateSidebarState({ selectedResourceGroupIds: [] });
    refetch();
  };

  const handleItemSelect = (selectedItem: string) => {
    updateSidebarState({
      selectedResourceGroupIds: getSelectedOptions(
        sidebarState.selectedResourceGroupIds ?? [],
        selectedItem
      ),
    });
  };

  return (
    <SidebarSelector
      name="resource group"
      title="Add Resource Group"
      onClose={handleClose}
      showValueInOptions
      onSubmit={handleSubmit}
      options={isSearchAllowed ? resourceGroupOptions : []}
      onItemSelect={handleItemSelect}
      loading={loading}
      selectedOptions={sidebarState.selectedResourceGroupIds ?? []}
      serverSideSearch
      onSearchChange={(val: string) => {
        updateSearchState({
          page: 1,
          filter: { ...searchState.filter, phrase: val },
        });
      }}
      noDataContent={
        !isSearchAllowed ? (
          <NoData message={getNoSearchResultsMessage()} />
        ) : undefined
      }
      renderPagination={
        <Pagination
          count={count}
          page={searchState.page}
          limit={searchState.limit}
          onPageChange={(p) => updateSearchState({ page: p })}
          onLimitChange={(l) => updateSearchState({ limit: l, page: 1 })}
          small
        />
      }
      isOpen
    />
  );
}
